import { useState, useEffect } from 'react';
import { Box, Heading, Input, Textarea, Button, FormControl, FormLabel, Select, useToast } from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import i18n from '../../config/i18n';

const Contact = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(user?.email || '');
  const [asunto, setAsunto] = useState(t('contact_asuntos_0'));
  const [mensaje, setMensaje] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Actualizar email cuando el usuario se carga
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  const asuntos = [
    t('contact_asuntos_0'),
    t('contact_asuntos_1'),
    t('contact_asuntos_2'),
    t('contact_asuntos_3'),
    t('contact_asuntos_4')
  ];

  const getDeviceInfo = () => {
    // Generar o recuperar custom_device_id
    let customDeviceId = localStorage.getItem('custom_device_id');
    if (!customDeviceId) {
      // Generar un ID único basado en timestamp + random
      customDeviceId = `device_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      localStorage.setItem('custom_device_id', customDeviceId);
    }

    const baseInfo = {
      user_agent: navigator.userAgent || null,
      accept_language: navigator.language || null,
      app_version: navigator.appVersion || null,
      platform: navigator.platform || null,
      vendor: navigator.vendor || null,
      ip_address: null, // Se obtiene en el backend
      ip_address_forwarded: null, // Se obtiene en el backend
      origin: window.location.origin,
      referer: document.referrer || null,
      connection_type: navigator.connection ? navigator.connection.effectiveType : null,
      accept_encoding: null, // Se obtiene en el backend
      x_requested_with: "XMLHttpRequest",
      custom_device_id: customDeviceId,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      screen_color_depth: window.screen.colorDepth,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      languages: navigator.languages ? navigator.languages.join(', ') : navigator.language,
      online: navigator.onLine,
      cookie_enabled: navigator.cookieEnabled,
      do_not_track: navigator.doNotTrack,
      hardware_concurrency: navigator.hardwareConcurrency || null,
      device_memory: navigator.deviceMemory || null,
      max_touch_points: navigator.maxTouchPoints || 0,
      latitude: null,
      longitude: null,
      location_accuracy: null,
      address: null
    };

    // Intentar obtener ubicación (solo si el usuario da permiso)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          baseInfo.latitude = position.coords.latitude;
          baseInfo.longitude = position.coords.longitude;
          baseInfo.location_accuracy = position.coords.accuracy;
          
          // Obtener dirección mediante geocodificación inversa
          try {
            const latlng = `${position.coords.latitude},${position.coords.longitude}`;
            const response = await fetch(`/api/maps/geocode/?latlng=${latlng}`);
            if (response.ok) {
              const data = await response.json();
              if (data.results && data.results.length > 0) {
                baseInfo.address = data.results[0].formatted_address;
              }
            }
          } catch (error) {
            console.log('Geocoding error:', error.message);
          }
        },
        (error) => {
          // Si el usuario rechaza o hay error, los valores quedan en null
          console.log('Geolocation error:', error.message);
        },
        { timeout: 5000, maximumAge: 0 }
      );
    }

    return baseInfo;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const device_info = getDeviceInfo();
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL || "";
      const res = await fetch(`${baseURL}/api/formulario-contacto/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, asunto, mensaje, device_info, idioma: i18n.language }),
      });
      if (res.ok) {
        toast({ title: t('contact_mensaje_enviado'), status: 'success', duration: 4000, isClosable: true });
        setMensaje('');
        // Redirigir a la home después de 1 segundo
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        toast({ title: t('contact_error_enviar'), status: 'error', duration: 4000, isClosable: true });
      }
    } catch {
      toast({ title: t('contact_error_red'), status: 'error', duration: 4000, isClosable: true });
    }
    setLoading(false);
  };

  return (
    <Box p={4}>
      <Heading as="h2" size="lg" mb={4}>
        {t('contact_titulo')}
      </Heading>

      <Box as="form" onSubmit={handleSubmit} maxW="md" mx="auto" mt={6} p={4} borderWidth="1px" borderRadius="lg">
        <FormControl mb={3} isRequired>
          <FormLabel>{t('contact_email')}</FormLabel>
          <Input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            isReadOnly={!!user?.email}
          />
        </FormControl>
        <FormControl mb={3} isRequired>
          <FormLabel>{t('contact_asunto')}</FormLabel>
          <Select value={asunto} onChange={e => setAsunto(e.target.value)}>
            {asuntos.map((a, idx) => (
              <option key={idx} value={a}>{a}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl mb={3} isRequired>
          <FormLabel>{t('contact_mensaje')}</FormLabel>
          <Textarea
            value={mensaje}
            onChange={e => setMensaje(e.target.value)}
            rows={5}
          />
        </FormControl>
        <Button type="submit" colorScheme="teal" isLoading={loading}>
          {t('contact_enviar')}
        </Button>
      </Box>
    </Box>
  );
};

export default Contact;
